import 'keen-slider/keen-slider.min.css'

import React, {useCallback, useEffect, useRef, useState} from 'react'
import {cx} from '@linaria/core'
import {AnimatePresence, motion} from 'framer-motion'
import {useKeenSlider} from 'keen-slider/react'

import {Button} from '@daedalus/atlas/Button'
import {Icon} from '@daedalus/atlas/Icon'
import {Image} from '@daedalus/atlas/Image'
import {Text} from '@daedalus/atlas/Text'
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'
import {ImageProvider} from '@daedalus/core/src/staticContent/types/ImageProvider'
import {useScrollDetector} from '@daedalus/core/src/utils/react/hooks/useScrollDetector'

import {CAROUSEL_LOADING, MIN_THUMBNAIL_IMAGE_COUNT} from './constant'
import {LightboxGalleryDesktopLoading} from './LightboxGalleryDesktopLoading'
import {
  CaptionContentWrapper,
  CarouselButtonWrapper,
  CarouselContainer,
  CarouselWrapper,
  ImageContainer,
  ImageNumberIndicator,
  LeftPanel,
  NoMoreImageWrapper,
  OfferContentWrapper,
  RightPanel,
  SliderWrapper,
  ThumbnailImageWrapper,
  ThumbnailWrapper,
  TopGradient,
  Wrapper
} from './styles'

export type CarouselActionType = 'NEXT' | 'PREV'

interface Props {
  images: string[]
  imageProvider: ImageProvider
  selectedImage?: string
  isLoading?: boolean
  offerContent?: React.ReactNode
  hotelDetailsContent?: React.ReactNode
  trackLightboxGalleryThumbnail?: () => void
  onLightboxGalleryCarouselSwipe?: () => void
}

export const ImageCarouselWithThumbnail: React.FC<Props> = ({
  images,
  isLoading,
  selectedImage,
  offerContent,
  hotelDetailsContent,
  imageProvider,
  trackLightboxGalleryThumbnail,
  onLightboxGalleryCarouselSwipe
}) => {
  const imageContainerRef = useRef(null)

  const [imageIndex, setImageIndex] = useState<number>(
    images?.indexOf(selectedImage ?? '') ?? 0
  )
  const [isLoaded, setLoaded] = useState(false)
  const [opacities, setOpacities] = React.useState<number[]>([])
  const isScrolling = useScrollDetector(imageContainerRef)

  useEffect(() => {
    trackLightboxGalleryThumbnail?.()
  }, [trackLightboxGalleryThumbnail])

  const handleScrollIntoView = useCallback((index: number) => {
    const element = document.getElementById(
      `image-${index === 0 ? index : index - 1}-thumbnail`
    )
    if (element) element.scrollIntoView({behavior: 'smooth', inline: 'center'})
    return
  }, [])

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    initial: images?.indexOf(selectedImage ?? '') ?? 0,
    slides: images.length,
    loop: true,
    slideChanged: slider => {
      const newIndex = slider.track.details.rel
      setImageIndex(newIndex)
      handleScrollIntoView(newIndex)
    },
    detailsChanged: changes => {
      const new_opacities = changes.track.details.slides.map(
        slide => slide.portion
      )
      setOpacities(new_opacities)
    },
    created: () => {
      setTimeout(() => setLoaded(true), CAROUSEL_LOADING)
    }
  })

  const handleArrowClick = useCallback(
    (direction: CarouselActionType) => {
      if (!isLoaded || !instanceRef?.current) return

      if (direction === 'PREV') {
        onLightboxGalleryCarouselSwipe?.()
        instanceRef.current.prev()
        return
      }
      onLightboxGalleryCarouselSwipe?.()
      instanceRef.current.next()
      return
    },
    [instanceRef, isLoaded, onLightboxGalleryCarouselSwipe]
  )

  const shouldLoadThisImage = useCallback(
    (imageIndex: number, selectedImage: number) => {
      switch (imageIndex) {
        case selectedImage:
        case selectedImage + 1:
        case selectedImage - 1:
        case images.length:
          return true
        default:
          return false
      }
    },
    [images]
  )

  const handleSelectThumbnail = useCallback(
    (newIndex: number) =>
      instanceRef?.current?.moveToIdx(newIndex, true, {duration: 0}),
    [instanceRef]
  )

  useEffect(() => {
    // Function to handle the keydown event
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        handleArrowClick('NEXT')
      } else if (event.key === 'ArrowLeft') {
        handleArrowClick('PREV')
      }
    }

    // Add the event listener to the window
    window.addEventListener('keydown', handleKeyDown)

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleArrowClick, isLoaded])

  useEffect(() => {
    setTimeout(() => setLoaded(true), CAROUSEL_LOADING)
  }, [])

  if (!isLoaded || isLoading) return <LightboxGalleryDesktopLoading />

  return (
    <Wrapper>
      <LeftPanel>
        <motion.div
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          transition={{duration: 0.2}}
          className="motion-element"
        >
          <CarouselWrapper>
            <SliderWrapper ref={sliderRef} className="fader">
              {images.map((image, index) => (
                <CarouselContainer
                  opacity={opacities[index]}
                  className="fader__slide"
                  key={image}
                  data-testId={`${
                    index === imageIndex ? 'active-' : ''
                  }carousel-${index}`}
                >
                  {shouldLoadThisImage(index, imageIndex) && (
                    <Image
                      url={image}
                      imageProvider={imageProvider}
                      isResponsive
                      backgroundSize="contain"
                      size="lightbox"
                    />
                  )}
                </CarouselContainer>
              ))}
            </SliderWrapper>
            <CaptionContentWrapper>
              {hotelDetailsContent}
              <ImageNumberIndicator variant="bodyS">
                {imageIndex + 1}/{images.length}
              </ImageNumberIndicator>
            </CaptionContentWrapper>
            {images.length > 1 && (
              <React.Fragment>
                <CarouselButtonWrapper className="--type-prev">
                  <Button
                    onClick={() => handleArrowClick('PREV')}
                    variant="secondary"
                    iconStart={<Icon name="ChevronLeft" />}
                    isRounded
                  />
                </CarouselButtonWrapper>
                <CarouselButtonWrapper className="--type-next">
                  <Button
                    onClick={() => handleArrowClick('NEXT')}
                    variant="secondary"
                    iconStart={<Icon name="ChevronRight" />}
                    isRounded
                  />
                </CarouselButtonWrapper>
              </React.Fragment>
            )}
          </CarouselWrapper>
        </motion.div>
      </LeftPanel>
      <RightPanel>
        <ImageContainer ref={imageContainerRef}>
          <AnimatePresence>
            {isScrolling && (
              <TopGradient
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
                transition={{duration: 0.2}}
              />
            )}
          </AnimatePresence>
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.2}}
          >
            <ThumbnailWrapper>
              {images.map((image, index) => (
                <ThumbnailImageWrapper
                  key={image}
                  id={`image-${index}-thumbnail`}
                  className={cx(index === imageIndex && 'isSelected')}
                  onClick={() => handleSelectThumbnail(index)}
                  data-testId={`${
                    index === imageIndex ? 'active-' : ''
                  }thumbnail-${index}`}
                >
                  <Image
                    borderRadius="xs"
                    url={image}
                    imageProvider={imageProvider}
                  />
                </ThumbnailImageWrapper>
              ))}
            </ThumbnailWrapper>
            {images.length >= MIN_THUMBNAIL_IMAGE_COUNT && (
              <NoMoreImageWrapper>
                <Text variant="bodyS">
                  <FormattedMessageWrapper
                    id="noMorePhotosToShow"
                    defaultMessage="No more photos to show"
                  />
                </Text>
              </NoMoreImageWrapper>
            )}
          </motion.div>
        </ImageContainer>
        {offerContent && (
          <OfferContentWrapper>{offerContent}</OfferContentWrapper>
        )}
      </RightPanel>
    </Wrapper>
  )
}
